import { Div, Text } from "atomize"
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
	AnimatePresence,
	animate,
	motion,
	useMotionValue,
	useMotionValueEvent,
	useReducedMotion,
} from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarDay, faCalendarWeek } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { schoolContext } from "../providers/school.provider"
import { lessonsContext } from "../providers/lessons.provider"
import Dropdown from "../atomize/Dropdown"
import { schoolMenuItems } from "../school/SchoolSwitcher"
import SchoolAvatar from "../school/SchoolAvatar"
import { canSwitchSchool } from "../utils/func.utils"
import { getLessonsSummary, getWeekDays } from "../utils/week.utils"
import { haptic } from "../utils/haptics.utils"
import {
	PHONE_HEADER_HEIGHT,
	PHONE_HEADER_TOP,
	PHONE_ISLAND_SIDE,
} from "../atoms/phoneBars"
import { usePhoneTitle } from "./PhonePageTitle"

// ──────────────────────────────────────────────────────────────────────────
// LA FASCIA IN CIMA AL TELEFONO
//
// Nelle lezioni è una riga sola, arancione, larga quanto lo schermo: a
// sinistra giorno o settimana, al centro il logo con sotto quante lezioni e
// quante ore ci sono in quello che si guarda, a destra il tondo del
// circolo. Le due colonne di lato sono larghe uguali, così il logo sta in
// mezzo anche quando una delle due è vuota.
//
// Fuori dalle lezioni (allievi, quaderno, profilo) è una pillola corta in
// alto a destra: il logo con sotto due numeri della pagina, e il tondo del
// circolo. Il titolo della pagina le sta di fianco, sulla stessa riga (vedi
// PhonePageTitle). Scorrendo giù la pillola si ritira e resta solo il
// tondo; torna lunga appena si scorre su, o si arriva in cima. Prima anche
// qui c'era la fascia larga, con solo il logo e il tondo: tanto spazio per
// niente, e sopra un titolo che ripeteva dove si era.
//
// Il tondo del circolo c'è sempre: con più circoli apre l'elenco per
// cambiarlo, con uno solo porta alla sua scheda nel profilo.
//
// Larga o corta è la stessa isola, che resta montata e cambia forma:
// passando da una pagina all'altra si allarga, si stringe e cambia i numeri
// sul posto, e il tondo resta dov'è. Prima a ogni pagina spariva e
// ricompariva cadendo dall'alto.
//
// Larga o corta, è un'isola come la barra in basso: staccata dai bordi e
// tutta tonda, con i comandi di lato che ne seguono la curva. Sopra l'isola
// resta una striscia di pagina, e quello che ci scorre sotto sfuma nel
// fondo invece di passare netto sotto l'orologio.
// ──────────────────────────────────────────────────────────────────────────

// il bianco velato su cui stanno i comandi della fascia
const VEIL = "rgba(255, 255, 255, 0.22)"

const VIEWS = [
	{ value: "day", label: "Giorno", icon: faCalendarDay },
	{ value: "week", label: "Settimana", icon: faCalendarWeek },
]

// Le misure della pillola sono in px perché la pagina le usa per tenersi
// libero il posto: quanto è larga è il margine di lato, più il tondo del
// circolo nel suo anello, più logo e numeri. In px anche gli angoli: mentre
// l'isola cambia forma framer-motion li ricalcola, e i rem non li sa
// ricalcolare.
//
// Larga o corta l'isola è alta uguale, con gli stessi angoli, lo stesso
// logo e il tondo allo stesso posto: cambiando pagina cambia solo la
// larghezza.
const SIDE_PADDING = 8 // mezzo rem, come i comandi dentro la curva
const PILL_RING = 44 // AVATAR_SIZE più l'anello velato, 3px per lato
const RADIUS = 30
const LOGO_HEIGHT = 24
// l'aria tra il logo e quello che gli sta di fianco
const INFO_PADDING = 12
// il logo è 525×164: largo il triplo abbondante di quanto è alto. Scritto,
// perché la pillola si misura prima che l'immagine sia arrivata
const LOGO_RATIO = 525 / 164
// Come cambia forma l'isola: con transizioni CSS sulla larghezza e su
// quanto si allargano i due lati, non con le animazioni di layout di
// framer-motion. Quelle misurano le cose come se stessero nella pagina, e
// con la pagina scorsa (cambiando sezione dalla barra in basso si torna in
// cima) facevano arrivare i pezzi dell'isola dal basso. Così invece è il
// browser a rifare la riga a ogni fotogramma: i pezzi si spostano in
// blocco con l'isola, e lo scorrimento non c'entra.
const MORPH_EASE = "cubic-bezier(0.22, 1, 0.36, 1)"
const MORPH_MS = 380
const FADE_MS = 160
// uscendo dalle lezioni giorno e settimana spariscono subito, prima che
// l'isola stringendosi li porti sopra il logo
const FADE_OUT_MS = 70
const morph = property => `${property} ${MORPH_MS}ms ${MORPH_EASE}`
const fade = (delay = 0, ms = FADE_MS) => `opacity ${ms}ms ease ${delay}ms`

function PhoneHeader() {
	const location = useLocation()
	const onLessons = location.pathname === "/lessons"
	const { schoolIDs, selectedSchool } = useContext(schoolContext)
	const { summary, setPillWidth } = usePhoneTitle()
	const canSwitch = canSwitchSchool(schoolIDs)
	const hasSchool = Boolean(selectedSchool)
	const reduceMotion = useReducedMotion()
	// nelle lezioni la fascia non si ritira: ha giorno e settimana
	const tucked = useTuckedOnScroll(location.pathname) && !onLessons
	const info = useRef(null)
	const [infoWidth, setInfoWidth] = useState(0)

	// Quanto sono larghi logo e numeri: ne viene la larghezza della pillola,
	// e il titolo della pagina si tiene libero quel posto (quello della
	// pillola allungata: quando si ritira il titolo è già scorso via).
	useLayoutEffect(() => {
		const measure = () => {
			const width = info.current?.offsetWidth ?? 0
			setInfoWidth(width)
			if (!onLessons) setPillWidth(SIDE_PADDING * 2 + PILL_RING + width)
		}
		measure()
		document.fonts?.ready.then(measure)
	}, [summary, onLessons, setPillWidth])

	// fuori dalle lezioni, senza circolo, non c'è niente da far vedere né da scegliere
	const shown = onLessons || hasSchool
	useEffect(() => {
		if (!shown) setPillWidth(0)
	}, [shown, setPillWidth])

	const width = onLessons
		? "100%"
		: `${SIDE_PADDING * 2 + PILL_RING + (tucked ? 0 : infoWidth)}px`
	const transition = reduceMotion ? "none" : undefined
	// Nelle lezioni i due lati si allargano uguali, così il logo sta in mezzo
	// anche quando uno dei due è vuoto; fuori non prendono spazio, e logo e
	// tondo stanno stretti a destra. Quanto si allargano cambia piano, e il
	// logo scivola dal centro a destra e indietro.
	const side = {
		flexGrow: onLessons ? 1 : 0,
		flexBasis: 0,
		display: "flex",
		alignItems: "center",
		transition: transition ?? morph("flex-grow"),
	}

	return (
		<>
			{/* la striscia sopra l'isola: la pagina che ci scorre sotto sfuma */}
			<div
				aria-hidden="true"
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					height: PHONE_HEADER_TOP,
					zIndex: 2,
					background: `linear-gradient(${theme.colors.background}, transparent)`,
					pointerEvents: "none",
				}}
			/>
			{shown && (
				// La striscia fissa in cima, larga quanto lo schermo meno i margini:
				// l'isola ci sta dentro, a destra
				<div
					style={{
						position: "fixed",
						top: PHONE_HEADER_TOP,
						left: PHONE_ISLAND_SIDE,
						right: PHONE_ISLAND_SIDE,
						zIndex: 2,
						display: "flex",
						justifyContent: "flex-end",
						pointerEvents: "none",
					}}>
					<motion.div
						data-phone-header
						initial={{ y: -20 }}
						animate={{ y: 0 }}
						style={{
							pointerEvents: "auto",
							width,
							maxWidth: "100%",
							boxSizing: "border-box",
							height: PHONE_HEADER_HEIGHT,
							background: theme.colors.primary,
							boxShadow: theme.shadows.accent,
							borderRadius: `${RADIUS}px`,
							display: "flex",
							alignItems: "center",
							// i pezzi stanno attaccati al bordo destro: lo spazio che
							// avanza mentre l'isola cambia larghezza resta a sinistra, e il
							// tondo non si muove. Sul telefono (Safari) quanto si allargano
							// i lati non si anima, cambia d'un colpo: senza questo, per
							// qualche fotogramma i pezzi si ammucchiavano a sinistra
							justifyContent: "flex-end",
							padding: `0 ${SIDE_PADDING}px`,
							transition: transition ?? morph("width"),
						}}>
						{/* giorno o settimana: nelle lezioni, e solo quando l'isola si è
						    già allargata, se no starebbe un attimo sopra il logo */}
						{/* chiuso dentro il suo lato: finché l'isola è stretta non
						    esce a sinistra, sopra il titolo della pagina */}
						<div style={{ ...side, justifyContent: "flex-start", minWidth: 0, overflow: "hidden" }}>
							<div
								aria-hidden={!onLessons}
								style={{
									opacity: onLessons ? 1 : 0,
									visibility: onLessons ? "visible" : "hidden",
									transition:
										transition ??
										(onLessons
											? `${fade(MORPH_MS / 2)}, visibility 0s`
											: `${fade(0, FADE_OUT_MS)}, visibility 0s ${FADE_OUT_MS}ms`),
								}}>
								<LessonsViewIcons />
							</div>
						</div>

						{/* logo e numeri: ritirandosi la pillola li chiude, e svaniscono */}
						<div
							data-testid="pill-info"
							aria-hidden={tucked}
							style={{
								flex: "0 1 auto",
								minWidth: 0,
								overflow: "hidden",
								opacity: tucked ? 0 : 1,
								transition: transition ?? fade(),
							}}>
							<div
								ref={info}
								style={{
									width: "max-content",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									// lo stesso spazio di lato nelle due misure: se cambiasse
									// d'un colpo, il tondo farebbe uno scatto
									padding: `0 ${INFO_PADDING}px`,
									whiteSpace: "nowrap",
								}}>
								<img
									src={require("../images/incampo_logo_header.png")}
									alt="inCampo"
									style={{
										display: "block",
										height: `${LOGO_HEIGHT}px`,
										width: `${Math.round(LOGO_HEIGHT * LOGO_RATIO)}px`,
									}}
								/>
								{onLessons ? (
									<LessonsSummary />
								) : (
									// la riga c'è anche vuota, mentre arrivano i numeri della
									// pagina nuova: il logo non si sposta
									summary != null && <SummaryLine>{summary || "\u00a0"}</SummaryLine>
								)}
							</div>
						</div>

						<div style={{ ...side, justifyContent: "flex-end", flexShrink: 0 }}>
							{canSwitch ? <SchoolPicker /> : hasSchool && <SchoolLink />}
						</div>
					</motion.div>
				</div>
			)}
			{/* nelle lezioni la fascia è fissa e prende il suo posto nella pagina;
			    fuori, il posto lo tiene il titolo della pagina (PhonePageTitle) */}
			{onLessons && (
				<Div h={`calc(${PHONE_HEADER_TOP} + ${PHONE_HEADER_HEIGHT})`} pos="relative" />
			)}
		</>
	)
}

// la riga sotto il logo, piccola e bianca: nelle lezioni e nelle altre pagine
function SummaryLine({ children }) {
	return (
		<Text
			textColor={theme.colors.white}
			textWeight="500"
			style={{ fontSize: "0.6875rem", lineHeight: 1.2, whiteSpace: "nowrap", marginTop: "-0.1rem" }}>
			{children}
		</Text>
	)
}

// Scorrendo giù la pillola si ritira, scorrendo su torna: conta la
// direzione, non il punto, così chi risale anche di poco la ritrova. In
// cima è sempre allungata, e cambiando pagina si riparte allungata. I
// movimenti piccoli si sommano finché non fanno abbastanza strada, così un
// dito che trema non la fa ballare.
const TOP_ZONE = 24
const SCROLL_STEP = 8

function useTuckedOnScroll(page) {
	const [tucked, setTucked] = useState(false)

	useEffect(() => {
		setTucked(false)
		let from = window.scrollY
		const onScroll = () => {
			const y = window.scrollY
			if (y < TOP_ZONE) setTucked(false)
			else if (y > from + SCROLL_STEP) setTucked(true)
			else if (y < from - SCROLL_STEP) setTucked(false)
			else return
			from = y
		}
		window.addEventListener("scroll", onScroll, { passive: true })
		return () => window.removeEventListener("scroll", onScroll)
	}, [page])

	return tucked
}

// Giorno o settimana, come due icone: le parole non lascerebbero posto al
// logo. Il nome di ognuna lo legge chi usa uno screen reader.
//
// Si tocca un'icona, oppure si trascina il cerchio bianco dall'una
// all'altra: lasciandolo va dove è più vicino. Il cerchio segue la vista
// anche quando cambia da altrove (le dita sulle lezioni, la barra laterale).
const ICON_WIDTH = 44
const ICON_HEIGHT = 38
const ICON_GAP = 2
const STEP = ICON_WIDTH + ICON_GAP
const SLIDE = { type: "spring", stiffness: 500, damping: 40 }

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

function LessonsViewIcons() {
	const { lessonsView, setLessonsView, setLessonToOpen } = useContext(lessonsContext)

	const viewIndex = Math.max(
		0,
		VIEWS.findIndex(view => view.value === lessonsView),
	)
	const thumbX = useMotionValue(viewIndex * STEP)
	// l'icona sotto il cerchio è arancione, l'altra bianca: mentre si
	// trascina il colore passa a metà strada, non al rilascio
	const [thumbIndex, setThumbIndex] = useState(viewIndex)
	useMotionValueEvent(thumbX, "change", x => setThumbIndex(Math.round(x / STEP)))

	useEffect(() => {
		animate(thumbX, viewIndex * STEP, SLIDE)
	}, [thumbX, viewIndex])

	// dove era il cerchio quando il dito l'ha toccato; e se
	// il tocco è stato un trascinamento, il clic che arriva al rilascio non
	// conta: la vista l'ha già scelta il cerchio
	const panFrom = useRef(0)
	const hasPanned = useRef(false)

	const changeView = view => {
		if (view === lessonsView) return
		// una lezione da aprire rimasta in sospeso non deve saltare fuori
		// al prossimo giorno che si guarda
		setLessonToOpen(null)
		setLessonsView(view)
		haptic("tick")
	}

	return (
		<motion.div
			// da dove parte il cerchio si legge quando il dito tocca: il primo
			// onPan arriva prima di onPanStart
			onPointerDown={() => {
				hasPanned.current = false
				panFrom.current = thumbX.get()
			}}
			onPanStart={() => (hasPanned.current = true)}
			onPan={(_, info) =>
				thumbX.set(clamp(panFrom.current + info.offset.x, 0, (VIEWS.length - 1) * STEP))
			}
			onPanEnd={() => {
				const index = clamp(Math.round(thumbX.get() / STEP), 0, VIEWS.length - 1)
				animate(thumbX, index * STEP, SLIDE)
				changeView(VIEWS[index].value)
			}}
			style={{
				position: "relative",
				display: "flex",
				padding: "3px",
				gap: `${ICON_GAP}px`,
				borderRadius: "999px",
				background: VEIL,
				// il trascinamento è nostro, non dello scorrimento della pagina
				touchAction: "none",
			}}>
			<motion.div
				aria-hidden="true"
				style={{
					position: "absolute",
					top: "3px",
					left: "3px",
					width: `${ICON_WIDTH}px`,
					height: `${ICON_HEIGHT}px`,
					borderRadius: "999px",
					background: theme.colors.white,
					x: thumbX,
				}}
			/>
			{VIEWS.map(({ value, label, icon }, i) => (
				<motion.button
					key={value}
					type="button"
					aria-label={label}
					aria-pressed={value === lessonsView}
					whileTap={{ scale: 0.92 }}
					onClick={() => !hasPanned.current && changeView(value)}
					style={{
						position: "relative",
						width: `${ICON_WIDTH}px`,
						height: `${ICON_HEIGHT}px`,
						padding: 0,
						border: 0,
						borderRadius: "999px",
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "transparent",
					}}>
					<FontAwesomeIcon
						icon={icon}
						color={i === thumbIndex ? theme.colors.primary : theme.colors.white}
						style={{ transition: "color 0.15s ease-in-out" }}
					/>
				</motion.button>
			))}
		</motion.div>
	)
}

// "4 lezioni · 4 ore" sotto il logo: del giorno o della settimana che si
// guarda, contate come l'elenco qui sotto. Senza lezioni resta solo il logo.
function LessonsSummary() {
	const { selectedDate, lessonsView, weekLessons } = useContext(lessonsContext)
	const { selectedSchool } = useContext(schoolContext)

	// Mentre il giorno o la settimana nuovi arrivano resta il riepilogo di
	// prima: sparire e ricomparire a ogni freccia farebbe saltare il logo.
	const lastSummary = useRef(null)

	let isLoaded, summary
	if (lessonsView === "week") {
		isLoaded = weekLessons != null
		summary =
			isLoaded && getLessonsSummary(getWeekDays(selectedDate).map(date => weekLessons[date]))
	} else {
		// anche il giorno, sul telefono, arriva con la sua settimana
		const dayLessons = weekLessons && (weekLessons[selectedDate] ?? [])
		isLoaded = dayLessons != null
		summary =
			isLoaded &&
			getLessonsSummary([dayLessons.filter(lesson => lesson.schoolID === selectedSchool)])
	}
	if (isLoaded) lastSummary.current = summary

	const shown = isLoaded ? summary : lastSummary.current
	if (!shown) return null

	return <SummaryLine>{shown}</SummaryLine>
}

// Il circolo scelto è un tondo, come l'account in Gmail: il suo logo o la
// sua iniziale. Toccandolo si apre l'elenco coi nomi interi; trascinandolo
// in giù si passa al circolo dopo, in su a quello prima, e per un attimo
// sotto compare il nome di quello in cui si è arrivati.
//
// I circoli girano in tondo: dopo l'ultimo torna il primo. Sono quelli del
// selettore, nel suo ordine: il principale in testa, gli archiviati fuori.
// alto quanto il cerchio di giorno / settimana, così le due cose ai lati
// dell'isola stanno alla stessa distanza dal suo bordo
const AVATAR_SIZE = 38
// quanto deve scorrere il dito, o quanto in fretta, perché il circolo cambi
const SWIPE_DISTANCE = 24
const SWIPE_VELOCITY = 300
// quanto il tondo segue il dito prima di lasciarlo
const SWIPE_FOLLOW = 0.35
const SWIPE_MAX_SHIFT = 10
// quanto resta il nome sotto il tondo dopo uno scorrimento
const NAME_FLASH_MS = 1600

const AVATAR_ROLL = {
	enter: direction => ({ y: -14 * direction, opacity: 0 }),
	center: { y: 0, opacity: 1 },
	exit: direction => ({ y: 14 * direction, opacity: 0 }),
}

function SchoolPicker() {
	const {
		schools,
		schoolIDs,
		primarySchoolID,
		isArchivedSchool,
		selectedSchool,
		getSelectedSchool,
		setSelectedSchool,
	} = useContext(schoolContext)
	const [isOpen, setIsOpen] = useState(false)
	// 1 se il circolo nuovo arriva da sopra (dito in giù), -1 da sotto
	const [direction, setDirection] = useState(1)
	const [flashedName, setFlashedName] = useState(null)

	const school = getSelectedSchool()
	const schoolName = school?.schoolName

	const shiftY = useMotionValue(0)
	// se il tocco è stato un trascinamento, il clic che arriva al rilascio
	// non deve aprire anche l'elenco
	const hasPanned = useRef(false)

	useEffect(() => {
		if (!flashedName) return
		const timer = setTimeout(() => setFlashedName(null), NAME_FLASH_MS)
		return () => clearTimeout(timer)
	}, [flashedName])

	const pick = schoolID => {
		setSelectedSchool(schoolID)
		setIsOpen(false)
	}

	const step = towards => {
		const index = schoolIDs.indexOf(selectedSchool)
		if (schoolIDs.length < 2 || index === -1) return

		const next = schoolIDs[(index + towards + schoolIDs.length) % schoolIDs.length]
		setDirection(towards)
		setIsOpen(false)
		setSelectedSchool(next)
		setFlashedName(schools[next]?.schoolName ?? null)
		haptic("tick")
	}

	return (
		<Div pos="relative">
			<Dropdown
				w="auto"
				h="auto"
				m="0"
				p="3px"
				border="0"
				bg="transparent"
				focusBg="transparent"
				direction="bottomright"
				isOpen={isOpen}
				openSuffix={null}
				closeSuffix={null}
				aria-label={`Cambia circolo: ${schoolName ?? ""}`}
				style={{ background: VEIL, borderRadius: "999px" }}
				onClick={() => setIsOpen(!isOpen)}
				menu={schoolMenuItems({
					schools,
					schoolIDs,
					selectedSchoolID: selectedSchool,
					primarySchoolID,
					isArchived: isArchivedSchool,
					onPick: pick,
				})}>
				<motion.div
					data-testid="school-swipe"
					onPointerDown={() => (hasPanned.current = false)}
					onPanStart={() => (hasPanned.current = true)}
					onPan={(_, info) =>
						shiftY.set(
							clamp(info.offset.y * SWIPE_FOLLOW, -SWIPE_MAX_SHIFT, SWIPE_MAX_SHIFT),
						)
					}
					onPanEnd={(_, info) => {
						animate(shiftY, 0, SLIDE)
						const { y } = info.offset
						const speed = info.velocity.y
						if (y > SWIPE_DISTANCE || speed > SWIPE_VELOCITY) step(1)
						else if (y < -SWIPE_DISTANCE || speed < -SWIPE_VELOCITY) step(-1)
					}}
					onClickCapture={event => {
						if (!hasPanned.current) return
						event.stopPropagation()
						event.preventDefault()
					}}
					style={{
						display: "flex",
						y: shiftY,
						// il trascinamento è nostro, non dello scorrimento della pagina
						touchAction: "none",
					}}>
					<AnimatePresence initial={false} mode="wait" custom={direction}>
						<motion.div
							key={selectedSchool ?? ""}
							custom={direction}
							variants={AVATAR_ROLL}
							initial="enter"
							animate="center"
							exit="exit"
							transition={{ duration: 0.12 }}
							style={{ display: "flex" }}>
							<SchoolAvatar school={school} size={AVATAR_SIZE} tone="onPrimary" />
						</motion.div>
					</AnimatePresence>
				</motion.div>
			</Dropdown>

			<AnimatePresence>
				{flashedName && !isOpen && (
					<motion.div
						key={flashedName}
						aria-live="polite"
						initial={{ opacity: 0, y: -4 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -4 }}
						transition={{ duration: 0.15 }}
						style={{
							position: "absolute",
							top: "calc(100% + 0.6rem)",
							right: 0,
							maxWidth: "70vw",
							padding: "0.4rem 0.75rem",
							borderRadius: "999px",
							background: theme.colors.surface,
							color: theme.colors.text,
							boxShadow: theme.shadows["top-3"],
							fontSize: "0.8125rem",
							fontWeight: 600,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							pointerEvents: "none",
						}}>
						{flashedName}
					</motion.div>
				)}
			</AnimatePresence>
		</Div>
	)
}

// Con un circolo solo non c'è niente da scegliere, ma il tondo resta: è il
// circolo dove si lavora, e toccandolo si va alla sua scheda nel profilo
// (dati, campi, maestri), che la pagina apre già in vista. Stesso anello
// velato del selettore, perché è la stessa cosa al suo posto.
function SchoolLink() {
	const navigate = useNavigate()
	const { getSelectedSchool } = useContext(schoolContext)
	const school = getSelectedSchool()

	return (
		<motion.button
			type="button"
			aria-label={`Il circolo: ${school?.schoolName ?? ""}`}
			whileTap={{ scale: 0.92 }}
			onClick={() => navigate("/profile", { state: { card: "school" } })}
			style={{
				display: "flex",
				padding: "3px",
				border: 0,
				borderRadius: "999px",
				background: VEIL,
				cursor: "pointer",
			}}>
			<SchoolAvatar school={school} size={AVATAR_SIZE} tone="onPrimary" />
		</motion.button>
	)
}

export default PhoneHeader
